// https://observablehq.com/@hausdorff/react@1177
import type htm from "htm";
import type React from "react";
import type ReactDOM from "react-dom/client";

export type DefineReact = (
	r: typeof React,
	rd: typeof ReactDOM,
	h: typeof htm
) => { render: unknown; component: unknown; forwardRef: unknown; memo: unknown; jsx: unknown };

export const define: DefineReact = (React, ReactDOM, htm) => {
	const createId = () => Number(Math.random().toString().slice(2)).toString(36);

	const setDisplayName = (f, name) => {
		f.displayName = name;
		return f;
	};

	const render = (_render, keyOrElement) => {
		return cache("elements", String(_render), keyOrElement, {
			init: (state) => {
				return {
					renderer: () => {
						return state.render(state.public);
					},
					id: createId(),
					element: document.createElement("div"),
					root: null,
					public: {
						useSetter: (setterOrValue, deps) => {
							if (typeof setterOrValue === "function") {
								/* eslint-disable react-hooks/rules-of-hooks */
								React.useEffect(
									() =>
										state.public.setValue(
											setterOrValue(state.public.getValue())
										),
									deps
								);
							} else {
								/* eslint-disable react-hooks/rules-of-hooks */
								React.useEffect(
									() => state.public.setValue(setterOrValue),
									[setterOrValue]
								);
							}
						},
						getValue: () => state.element.value,
						setValue: (value) => {
							state.element.value = value;
							state.element.dispatchEvent(new CustomEvent("input"));
						},
					},
				};
			},
			update: { render: _render },
			effect: (state) => {
				state.root ??= ReactDOM.createRoot(state.element);
				return state.root.render(React.createElement(state.renderer));
			},
			return: (state) => state.element,
		});
	};

	const component = (_render, name, keyOrPrevious) => {
		// @ts-ignore
		return cache("components", String(_render), keyOrPrevious, {
			init: (_state, funcFor) => ({
				component: setDisplayName(funcFor("render"), name),
			}),
			update: { render: _render },
			return: (state) => state.component,
		});
	};

	const jsx = (...args) => {
		const _jsx = htm.bind(React.createElement);

		try {
			//@ts-ignore
			return _jsx(...args);
		} catch (e) {
			if (e instanceof Error) {
				// throw a clearer error
				let stack = e.stack;
				if (!stack?.includes(e.message)) {
					stack = `${e.constructor.name}: ${e.message}\n${stack}`;
				}
				throw new SyntaxError("JSX syntax error\n" + stack);
			}
		}
	};

	const memo = (_render, arePropsEqual, name, keyOrElement) => {
		return cache(
			"memos",
			JSON.stringify([String(_render), String(arePropsEqual)]),
			keyOrElement,
			//@ts-ignore
			{
				init: (_state, funcFor) => ({
					component: React.memo(
						setDisplayName(funcFor("render"), name),
						funcFor("arePropsEqual")
					),
				}),
				update: { render: _render, arePropsEqual },
				return: (state) => state.component,
			}
		);
	};

	const forwardRef = (_render, name, keyOrElement) => {
		//@ts-ignore
		cache("forwardRefs", String(_render), keyOrElement, {
			init: (_state, funcFor) => ({
				component: React.forwardRef(setDisplayName(funcFor("render"), name)),
			}),
			update: { render: _render },
			return: (state) => state.component,
		});
	};

	const cache = (type, key, ctx, { init, update, effect, return: returnValue }) => {
		const fullKey = typeof ctx === "string" ? key + "\n" + ctx : key;

		const prevResult = typeof ctx === "object" || typeof ctx === "function" ? ctx : null;

		const updateAndReturn = (state) => {
			Object.assign(state, update);
			if (effect) {
				effect(state);
			}
			const ret = returnValue(state);

			return ret;
		};

		if (prevResult) {
			return updateAndReturn(prevResult);
		}

		if (!cache[type]) {
			cache[type] = Object.create(null);
		}

		if (!cache[type][fullKey]) {
			const state = Object.create(null);
			Object.assign(
				state,
				init(
					state,
					(subkey) =>
						(...args) =>
							state[subkey](...args)
				)
			);
			cache[type][fullKey] = state;
		}
		return updateAndReturn(cache[type][fullKey]);
	};

	return {
		render,
		component,
		forwardRef,
		memo,
		jsx,
	};
};
